import { type ShortIdProviderProps, useShortIdContext } from '@air/provider-short-id';

import { usePublicShowcaseActiveSubPage } from '~/components/v2/components/showcase/hooks/usePublicShowcaseActiveSubPage';
import { SHOWCASE_IMPLICIT_DEFAULT_BLOCKS } from '~/components/v2/components/showcase/showcaseContentBlocks';

export type PublicShowcaseActivePageContent = NonNullable<ShortIdProviderProps['presentationContent']>;

/**
 * The body a never-authored root page stands in for. Module scope so the reference is stable across
 * renders — consumers memoize against it.
 */
const IMPLICIT_DEFAULT_CONTENT: PublicShowcaseActivePageContent = { blocks: SHOWCASE_IMPLICIT_DEFAULT_BLOCKS };

/**
 * The body of the page the viewer is currently on, ALWAYS present: the link's root
 * `presentationContent` on the root page, the sub-page's own `content` on a sub-page, and the
 * implicit default listing blocks when the root page has never been authored.
 *
 * That last substitution is the ONE designed meaning of an absent `presentationContent` — "never
 * authored" — and it happens HERE, once, because every surface that renders a showcase body reads
 * through this hook: the live page, the Preview iframe, and the editor's Edit-tab canvas (which
 * feeds the draft's content into the canvas subtree's ShortIdProvider). Substituting per consumer
 * would let one surface keep an older render and flip when another authored.
 *
 * `{ blocks: [] }` is untouched: a deliberately-empty page is authored content, not an absence.
 */
export const usePublicShowcaseActivePageContent = (): PublicShowcaseActivePageContent => {
  const { presentationContent } = useShortIdContext();
  const activeSubPage = usePublicShowcaseActiveSubPage();

  // A sub-page always carries a body on the wire — the backend seeds `content` at create time — so
  // only the root page can be unauthored.
  const content = activeSubPage ? activeSubPage.content : presentationContent;

  return content ?? IMPLICIT_DEFAULT_CONTENT;
};
